import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import AuthImg from "@/components/AuthImg";
import { toast } from "sonner";
import {
  Users, Loader2, Globe, Sparkles, ExternalLink, Save, Plus, X,
  Award, MapPin, Languages, Megaphone,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SPECIALIZATIONS = [
  "Haftpflicht", "Kasko", "Wertgutachten", "Hagelschaden", "Oldtimer",
  "Unfallrekonstruktion", "Wiederbeschaffungswert", "Reparaturkalkulation",
  "Lkw / Nutzfahrzeuge", "Motorrad", "Wohnmobil", "E-Fahrzeug",
];
const LANGUAGES = ["Deutsch", "Englisch", "Türkisch", "Russisch", "Polnisch", "Arabisch", "Italienisch"];

export default function AdminPartner() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user being edited
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.users);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const toggleListed = async (u, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/users/${u.id}/directory`, { is_directory_listed: value });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data.user : x)));
      toast.success(value ? "Im öffentlichen Verzeichnis gelistet" : "Aus Verzeichnis entfernt");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const saveDetails = async (payload) => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/users/${editing.id}/directory`, payload);
      setUsers((prev) => prev.map((x) => (x.id === editing.id ? data.user : x)));
      toast.success("Partner-Daten gespeichert");
      setEditing(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const listed = users.filter((u) => u.is_directory_listed);
  const eligible = users.filter((u) => !u.is_directory_listed && u.status === "active" && u.subscription_status === "active");

  return (
    <Layout title="Partner-Verzeichnis" subtitle="Öffentliche Profile freischalten und verwalten">
      <div className="max-w-6xl space-y-8">
        {/* Hero */}
        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-cyan-50 p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
              <Megaphone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Öffentliche Partner-Landingpages</h2>
              <p className="text-sm text-zinc-700 mt-1 max-w-3xl">
                Schalte aktive, abonnierte Gutachter ins öffentliche Verzeichnis frei. Jeder gelistete Partner erhält automatisch
                eine SEO-optimierte Landingpage unter <code className="font-mono text-emerald-700">/partner/&lt;slug&gt;</code> —
                vollständig gepflegt durch das Easy-Gutachter-Team.
              </p>
            </div>
          </div>
        </div>

        {/* Listed */}
        <section data-testid="admin-partner-listed">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold tracking-tight">Aktuell gelistet ({listed.length})</h3>
            <a href="/partner" target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm">
                <Globe className="h-3.5 w-3.5 mr-1.5" /> Verzeichnis öffnen
              </Button>
            </a>
          </div>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
          ) : listed.length === 0 ? (
            <Card className="p-10 text-center text-zinc-500" data-testid="admin-partner-listed-empty">
              Noch kein Partner im öffentlichen Verzeichnis.
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listed.map((u) => (
                <ListedCard key={u.id} u={u} onToggle={(v) => toggleListed(u, v)} onEdit={() => setEditing(u)} saving={saving} />
              ))}
            </div>
          )}
        </section>

        {/* Eligible (active + abo, not yet listed) */}
        <section data-testid="admin-partner-eligible">
          <h3 className="font-display text-lg font-bold tracking-tight mb-4">Bereit zur Freischaltung ({eligible.length})</h3>
          {eligible.length === 0 ? (
            <Card className="p-6 text-center text-sm text-zinc-500">
              Keine weiteren Kandidaten — nur aktive User mit gültigem Abo erscheinen hier.
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {eligible.map((u) => (
                <EligibleCard key={u.id} u={u} onActivate={() => toggleListed(u, true)} onConfigure={() => setEditing(u)} saving={saving} />
              ))}
            </div>
          )}
        </section>
      </div>

      <EditDialog open={!!editing} user={editing} onClose={() => setEditing(null)} onSave={saveDetails} saving={saving} />
    </Layout>
  );
}

function PartnerAvatar({ u, size = "h-12 w-12" }) {
  return (
    <div className={cn(size, "rounded-full overflow-hidden bg-white border border-zinc-200 flex items-center justify-center shrink-0")} data-testid={`partner-avatar-${u.id}`}>
      {u.avatar_path ? (
        <AuthImg path={u.avatar_path} className="h-full w-full object-cover" alt={u.name} />
      ) : (
        <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-full w-full object-contain p-1.5" />
      )}
    </div>
  );
}

function ListedCard({ u, onToggle, onEdit, saving }) {
  return (
    <Card className="p-5 hover:border-emerald-300 transition" data-testid={`admin-partner-card-${u.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <PartnerAvatar u={u} />
          <div className="min-w-0">
            <div className="font-display font-bold tracking-tight truncate">{u.name}</div>
            <div className="text-xs text-zinc-500 truncate">{u.city ? `${u.city} · ${u.email}` : u.email}</div>
            <div className="font-mono text-[11px] text-emerald-700 mt-1.5 truncate">/partner/{u.directory_slug}</div>
          </div>
        </div>
        <Switch checked={u.is_directory_listed} onCheckedChange={onToggle} disabled={saving} />
      </div>
      <div className="flex flex-wrap gap-1 mt-3">
        {(u.specializations || []).slice(0, 3).map((s) => (
          <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
        ))}
        {(u.specializations || []).length === 0 && (
          <span className="text-[11px] text-amber-600">Spezialisierungen fehlen</span>
        )}
      </div>
      <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-100">
        <Button size="sm" variant="outline" onClick={onEdit} className="flex-1" data-testid={`admin-edit-${u.id}`}>
          <Save className="h-3.5 w-3.5 mr-1" /> Bearbeiten
        </Button>
        <a href={`/partner/${u.directory_slug}`} target="_blank" rel="noreferrer">
          <Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button>
        </a>
      </div>
    </Card>
  );
}

function EligibleCard({ u, onActivate, onConfigure, saving }) {
  return (
    <Card className="p-5 border-dashed" data-testid={`admin-partner-eligible-${u.id}`}>
      <div className="flex items-start gap-3 min-w-0">
        <PartnerAvatar u={u} />
        <div className="min-w-0">
          <div className="font-display font-bold tracking-tight truncate">{u.name}</div>
          <div className="text-xs text-zinc-500 truncate">{u.city ? `${u.city} · ${u.email}` : u.email}</div>
          <div className="text-[11px] text-zinc-400 mt-1">
            <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Aktiv · Abo</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={onActivate} disabled={saving} className="brand-gradient-bg text-white flex-1" data-testid={`admin-activate-${u.id}`}>
          <Globe className="h-3.5 w-3.5 mr-1" /> Freischalten
        </Button>
        <Button size="sm" variant="outline" onClick={onConfigure} disabled={saving}>
          Bearbeiten
        </Button>
      </div>
    </Card>
  );
}

function EditDialog({ open, user, onClose, onSave, saving }) {
  const [data, setData] = useState({});
  useEffect(() => {
    if (!user) return;
    setData({
      directory_slug: user.directory_slug || "",
      specializations: user.specializations || [],
      languages: user.languages || ["Deutsch"],
      service_radius_km: user.service_radius_km || 50,
      service_postal_codes: user.service_postal_codes || [],
      bio_short: user.bio_short || "",
      bio_long: user.bio_long || "",
      geo_lat: user.geo_lat || "",
      geo_lng: user.geo_lng || "",
    });
  }, [user]);

  if (!user) return null;

  const toggleArr = (key, value) => {
    setData((d) => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };
  const addPlz = (raw) => {
    const cleaned = raw.split(/[,\s]+/).map((s) => s.trim()).filter((s) => /^\d{4,5}$/.test(s));
    setData((d) => ({ ...d, service_postal_codes: Array.from(new Set([...(d.service_postal_codes || []), ...cleaned])) }));
  };
  const removePlz = (plz) => setData((d) => ({ ...d, service_postal_codes: d.service_postal_codes.filter((p) => p !== plz) }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-partner-edit-dialog">
        <DialogHeader>
          <DialogTitle>Partner-Profil bearbeiten — {user.name}</DialogTitle>
          <DialogDescription>Diese Daten erscheinen 1:1 auf der öffentlichen Landingpage.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="URL-Slug">
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-500 text-sm font-mono">/partner/</span>
                <Input value={data.directory_slug || ""} onChange={(e) => setData({ ...data, directory_slug: e.target.value })} placeholder="easy-gutachter-…" data-testid="edit-slug" />
              </div>
            </Field>
            <Field label="Service-Radius (km)">
              <Input type="number" value={data.service_radius_km || 0} onChange={(e) => setData({ ...data, service_radius_km: Number(e.target.value) || 0 })} data-testid="edit-radius" />
            </Field>
            <Field label="Geo Latitude">
              <Input type="number" step="any" value={data.geo_lat || ""} onChange={(e) => setData({ ...data, geo_lat: e.target.value === "" ? null : Number(e.target.value) })} placeholder="52.5200" data-testid="edit-lat" />
            </Field>
            <Field label="Geo Longitude">
              <Input type="number" step="any" value={data.geo_lng || ""} onChange={(e) => setData({ ...data, geo_lng: e.target.value === "" ? null : Number(e.target.value) })} placeholder="13.4050" data-testid="edit-lng" />
            </Field>
          </div>

          <Field label="Spezialisierungen">
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleArr("specializations", s)}
                  className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border transition",
                    (data.specializations || []).includes(s)
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-emerald-300")}
                  data-testid={`edit-spec-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Sprachen">
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggleArr("languages", l)}
                  className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border transition",
                    (data.languages || []).includes(l)
                      ? "bg-cyan-600 text-white border-cyan-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-cyan-300")}
                  data-testid={`edit-lang-${l}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Versorgte Postleitzahlen">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(data.service_postal_codes || []).map((plz) => (
                <span key={plz} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 px-2 py-1 rounded text-xs font-mono">
                  {plz}
                  <button onClick={() => removePlz(plz)} type="button"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <Input
              placeholder="PLZ eingeben (kommagetrennt) und Enter drücken — z. B. 10115, 10117, 12101"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addPlz(e.currentTarget.value);
                  e.currentTarget.value = "";
                }
              }}
              data-testid="edit-plz-input"
            />
          </Field>

          <Field label="Kurz-Beschreibung (Hero-Untertitel — max. 280 Zeichen)">
            <Textarea
              value={data.bio_short || ""}
              onChange={(e) => setData({ ...data, bio_short: e.target.value.slice(0, 280) })}
              rows={2}
              placeholder="Ihr unabhängiger Kfz-Sachverständiger in Berlin. Schadenbegutachtung in 24 Stunden."
              data-testid="edit-bio-short"
            />
            <div className="text-[10px] text-zinc-400 mt-0.5 text-right">{(data.bio_short || "").length}/280</div>
          </Field>

          <Field label="Über den Partner (Lang-Text für die Profil-Seite)">
            <Textarea
              value={data.bio_long || ""}
              onChange={(e) => setData({ ...data, bio_long: e.target.value })}
              rows={6}
              placeholder="Ich bin … mit Sitz in …. Mit über X Jahren Erfahrung …"
              data-testid="edit-bio-long"
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => onSave(data)} disabled={saving} className="brand-gradient-bg text-white" data-testid="edit-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
